import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

export function VehicleNotesTab({ vehicleId, editAllowed }) {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [priority, setPriority] = useState("normal");
  const [pinned, setPinned] = useState(false);
  const reload = useCallback(async () => {
    const { data } = await api.get(`/vehicles/${vehicleId}/notes`);
    setItems(data.items || []);
  }, [vehicleId]);
  useEffect(() => { reload(); }, [reload]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/vehicles/${vehicleId}/notes`,
        { note: text.trim(), priority, pinned });
      setText(""); setPriority("normal"); setPinned(false); setAdding(false);
      await reload();
      toast.success("Note added");
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const remove = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/vehicles/${vehicleId}/notes/${n.id}`);
      await reload();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const togglePin = async (n) => {
    try {
      await api.put(`/vehicles/${vehicleId}/notes/${n.id}`, { pinned: !n.pinned });
      await reload();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4" data-testid="vd-notes">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-lg font-semibold">Internal Notes</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Private staff notes about this vehicle, never visible to customers or other businesses.
          </p>
        </div>
        {editAllowed && !adding && (
          <button onClick={() => setAdding(true)} data-testid="vd-notes-add"
            className="px-3 py-2 rounded-lg bg-[#1B4332] text-white hover:bg-[#2D6A4F] text-sm inline-flex items-center gap-2">
            <Plus className="w-3.5 h-3.5" /> Add note
          </button>
        )}
      </div>

      {adding && (
        <form onSubmit={submit} data-testid="vd-notes-form" className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3">
          <textarea data-testid="vd-notes-text" rows={3} required value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Battery weak: replace before next 3-day trip."
            className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]" />
          <div className="flex flex-wrap items-center gap-3">
            <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="vd-notes-priority"
              className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white text-sm">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
            <label className="text-sm inline-flex items-center gap-2">
              <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} /> Pin to top
            </label>
            <button type="submit" data-testid="vd-notes-save"
              className="ml-auto px-4 py-2 rounded-lg bg-[#1B4332] text-white hover:bg-[#2D6A4F] text-sm font-medium">Save note</button>
            <button type="button" onClick={() => { setAdding(false); setText(""); }}
              className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">Cancel</button>
          </div>
        </form>
      )}

      {items === null ? <div className="text-sm text-slate-500">Loading…</div>
        : items.length === 0 ? (
          <div className="text-sm text-slate-500" data-testid="vd-notes-empty">
            No internal notes yet. Use this space for maintenance reminders, customer feedback, or operational rules for this vehicle.
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((n) => (
              <li key={n.id} data-testid={`vd-note-${n.id}`}
                className={`rounded-lg border p-3 ${n.pinned ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm whitespace-pre-line">{n.note}</div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      {n.created_by_name || "staff"} · {new Date(n.created_at).toLocaleString()}
                      {n.priority && n.priority !== "normal" && (
                        <span className={`ml-2 px-2 py-0.5 rounded-full ${n.priority === "high" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"} capitalize`}>
                          {n.priority}
                        </span>
                      )}
                    </div>
                  </div>
                  {editAllowed && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => togglePin(n)} data-testid={`vd-note-${n.id}-pin`}
                        className="text-[11px] text-slate-600 hover:underline">
                        {n.pinned ? "Unpin" : "Pin"}
                      </button>
                      <button onClick={() => remove(n)} data-testid={`vd-note-${n.id}-delete`}
                        className="text-[11px] text-red-600 hover:underline">Delete</button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}
